import { Card, CardAction, CardContent, CardHeader, CardTitle, ScrollArea } from '@ibsys/kit';
import { FC, ReactNode } from 'react';
import { DASHBOARD_BLOCK_MAX_HEIGHT_CLASS, cn } from '@/shared/lib';
import { TruncatedText } from '@/shared/ui';
import { useChartCardHeaderAction } from './ChartCardHeaderContext';

type Props = {
  title: string;
  /**
   * Прокручивать содержимое внутри карточки при переполнении. Отключается у блоков со своей
   * прокруткой (таблицы): вложенная прокрутка внутри прокрутки неуправляема мышью.
   */
  scrollable?: boolean;
  children: ReactNode;
};

/**
 * Карточка диаграммы пользовательского дашборда: заголовок и содержимое, ограниченное отведённой
 * диаграмме высотой и общим потолком высоты блоков дашборда.
 *
 * Не поместившееся содержимое прокручивается внутри самой диаграммы — по каждой стороне отдельно
 * и только там, где места не хватило: узкой доле ширины широкий тип отдаётся прокруткой, а не
 * сжатием, зато вставшая целиком диаграмма полос не показывает вовсе.
 *
 * Содержимое со своей прокруткой прокручивается и по месту, которое оно за собой держит: у
 * наименьших заданных высот таблица не сжимается до одной шапки, а выходит за отведённое ей место,
 * и до её строк добираются прокруткой содержимого диаграммы.
 *
 * Рядом с названием встаёт управление диаграммой — переключатель её разреза: он приходит
 * контекстом, поэтому представлению типа о нём знать незачем.
 */
export const ChartCardShell: FC<Props> = ({ title, scrollable = true, children }) => {
  const headerAction = useChartCardHeaderAction();

  return (
    <Card className="min-h-0 overflow-hidden">
      {/*
        Управление шапки встаёт в отведённое ему китом место (`CardAction` — вторая колонка
        шапки), а раскладку шапки задаёт потребитель: своего расположения кит ей не навязывает.
      */}
      <CardHeader className={cn('shrink-0', headerAction && 'grid')}>
        <CardTitle className="min-w-0">
          <TruncatedText>{title}</TruncatedText>
        </CardTitle>
        {headerAction && <CardAction>{headerAction}</CardAction>}
      </CardHeader>
      <CardContent className={cn('min-h-0 flex-1', !scrollable && 'overflow-y-auto overflow-x-hidden')}>
        {scrollable ? (
          <ScrollArea className={cn('h-full', DASHBOARD_BLOCK_MAX_HEIGHT_CLASS)}>{children}</ScrollArea>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
};
